
import React, { useRef, useMemo, useEffect, useState } from "react";
import { OrbitControls, Text, ContactShadows, Decal, Html } from "@react-three/drei";
import { useLocation } from "react-router-dom";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";
import { useSelector } from "react-redux";
import { particular_Glb } from "../../Services/Operations/ProductAPI";
import "../../Styles/ViewGlbPage.css";

const SkeletonLoader = () => {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f8f9fa",
        zIndex: 9999,
        gap: "20px",
      }}
    >
      {/* Title Skeleton */}
      <div
        style={{
          width: "300px",
          height: "40px",
          backgroundColor: "#e9ecef",
          borderRadius: "8px",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      />
      
      {/* Canvas Area Skeleton */}
      <div
        style={{
          width: "80%",
          maxWidth: "800px",
          height: "60vh",
          backgroundColor: "#e9ecef",
          borderRadius: "12px",
          position: "relative",
          overflow: "hidden",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      >
        {/* Shimmer Effect */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
            animation: "shimmer 2s infinite",
          }}
        />
        
        {/* 3D Model Placeholder */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "200px",
            height: "250px",
            backgroundColor: "#dee2e6",
            borderRadius: "20px",
            animation: "pulse 1.5s ease-in-out infinite 0.2s",
          }}
        />
      </div>
      
      {/* Loading Text */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          color: "#6c757d",
          fontSize: "16px",
          fontWeight: "500",
        }}
      >
        <div
          style={{
            width: "20px",
            height: "20px",
            border: "2px solid #dee2e6",
            borderTop: "2px solid #007bff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        Loading your customized jersey...
      </div>
      
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.6;
            }
          }
          
          @keyframes shimmer {
            0% {
              left: -100%;
            }
            100% {
              left: 100%;
            }
          }
          
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};

const GlbViewer = ({ customizeFile, settings, onLoaded }) => {
  const { configUrl } = useSelector((state) => state.cart);
  const gltf = useLoader(GLTFLoader, `${configUrl}${customizeFile}`);
  const groupRef = useRef();
  const [parts, setParts] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    textures: false,
    parts: false,
    gltf: false
  });

  // Texture refs
  const frontTextTextureRef = useRef();
  const backTextTextureRef = useRef();
  const frontNumberTextureRef = useRef();
  const backNumberTextureRef = useRef();

  // Logo textures
  const logoTexture = useMemo(() => {
    const texture = new THREE.TextureLoader().load(settings.frontLogo);
    texture.needsUpdate = true;
    return texture;
  }, [settings.frontLogo]);

  const logoTextureBack = useMemo(() => {
    const texture = new THREE.TextureLoader().load(settings.backLogo);
    texture.needsUpdate = true;
    return texture;
  }, [settings.backLogo]);

  // Initialize canvas textures
  useEffect(() => {
    const createCanvasTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 512;
      const texture = new THREE.CanvasTexture(canvas);
      return { canvas, texture };
    };

    frontTextTextureRef.current = createCanvasTexture().texture;
    backTextTextureRef.current = createCanvasTexture().texture;
    frontNumberTextureRef.current = createCanvasTexture().texture;
    backNumberTextureRef.current = createCanvasTexture().texture;
  }, []);

  // Update text textures
  const updateTextTexture = (textureRef, text, partName, Colors, font) => {
    if (!text || !textureRef.current) return;

    const canvas = textureRef.current.image;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Canvas context not found.');
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const colorMapping = {
      frontText: 'front',
      backText: 'back',
      frontNumber: 'frontNumber',
      backNumber: 'backNumber',
    };

    const colorKey = colorMapping[partName] || partName;
    ctx.fillStyle = Colors?.[colorKey] || 'black';
    ctx.font = `bold 80px ${font}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    textureRef.current.needsUpdate = true;
  };

  // Update all textures when settings change
  useEffect(() => {
    if (!frontTextTextureRef.current) return;

    updateTextTexture(
      frontTextTextureRef,
      settings.frontText,
      'frontText',
      settings.Colors,
      settings?.textFonts?.front
    );
    updateTextTexture(
      backTextTextureRef,
      settings.backText,
      'backText',
      settings.Colors,
      settings?.textFonts?.back
    );
    updateTextTexture(
      frontNumberTextureRef,
      settings.frontNumber,
      'frontNumber',
      settings.Colors,
      settings?.numberFonts?.front
    );
    updateTextTexture(
      backNumberTextureRef,
      settings.backNumber,
      'backNumber',
      settings.Colors,
      settings?.numberFonts?.back
    );

    setLoadingStates(prev => ({ ...prev, textures: true }));
  }, [settings]);

  // Extract parts from GLTF
  useEffect(() => {
    const extractedParts = [];
    gltf.scene.traverse((child) => {
      if (child.isMesh) {
        const material = child.material || new THREE.MeshStandardMaterial({ color: 'red' });
        material.side = THREE.DoubleSide;
        material.needsUpdate = true;

        extractedParts.push({
          name: child.name,
          geometry: child.geometry,
          material: material,
        });
      }
    });
    setParts(extractedParts);
    setLoadingStates(prev => ({ ...prev, parts: true }));
  }, [gltf]);

  // Update materials when parts change
  useEffect(() => {
    if (parts.length > 0) {
      parts.forEach((part) => {
        if (part.material.map) {
          part.material.map.needsUpdate = true;
        }
        part.material.needsUpdate = true;
      });
      setLoadingStates(prev => ({ ...prev, gltf: true }));
    }
  }, [parts]);

  // Animation frame updates
  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.position.set(...settings.position);
      groupRef.current.rotation.set(...settings.rotation);
      groupRef.current.scale.set(settings.scale, settings.scale, settings.scale);
    }
  });

  // Notify parent when loaded
  useEffect(() => {
    if (loadingStates.textures && loadingStates.parts && loadingStates.gltf) {
      onLoaded();
    }
  }, [loadingStates, onLoaded]);

  return (
    <group dispose={null} scale={settings.overallScale}>
      <group
        position={settings.position}
        rotation={settings.rotation}
        scale={settings.scale}
        ref={groupRef}
      >
        {parts
          .filter((part) => part.name !== "LOGO")
          .map((part, index) => (
            <mesh
              key={index}
              castShadow
              receiveShadow
              geometry={part.geometry}
              material={part.material}
            >
              <Decal
                position={settings.frontTextPosition}
                rotation={settings.frontTextRotation}
                scale={settings.frontTextScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={frontTextTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.frontNumberPosition}
                rotation={settings.frontNumberRotation}
                scale={settings.frontNumberScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={frontNumberTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.backTextPosition}
                rotation={settings.backTextRotation}
                scale={settings.backTextScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={backTextTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.backNumberPosition}
                rotation={settings.backNumberRotation}
                scale={settings.backNumberScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial
                  map={backNumberTextureRef.current}
                  transparent
                />
              </Decal>

              <Decal
                position={settings.frontLogoPosition}
                rotation={settings.frontLogoRotation}
                scale={settings.frontLogoScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial map={logoTexture} transparent />
              </Decal>

              <Decal
                position={settings.backLogoPosition}
                rotation={settings.backLogoRotation}
                scale={settings.backLogoScale}
                depthTest
                depthWrite={false}
              >
                <meshStandardMaterial map={logoTextureBack} transparent />
              </Decal>
            </mesh>
          ))}
      </group>
      {parts
        .filter((part) => part.name === "LOGO")
        .map((part, index) => (
          <mesh
            key={index}
            castShadow
            receiveShadow
            geometry={part.geometry}
            material={part.material}
            position={settings.logoPosition}
            rotation={settings.logoRotation}
            scale={settings.logoScale}
          />
        ))}
    </group>
  );
};

const ViewGlbPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { tokenDetails } = useSelector((state) => state.user);
  const { configUrl } = useSelector((state) => state.cart);
  const [glb, setGlb] = useState(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encodedGlbId = queryParams.get("glbId");

  useEffect(() => {
    const userOrder = async () => {
      try {
        const formData = new FormData();
        formData.append("itemId", encodedGlbId);
        const response = await particular_Glb(formData);
        if (response.success === true) {
          setGlb(response.data);
        }
      } catch (error) {
        console.error("Error getting orders:", error);
      }
    };
    userOrder();
  }, [encodedGlbId]);

  const handleLoaded = () => {
    setIsLoading(false);
  };

  return (
    <div className="customizer-container">
      <h1 className="title">Your Customized Jersey</h1>
      {isLoading && <SkeletonLoader />}
      <Canvas
        className="jersey-canvas"
        style={{ width: "100%", height: "80vh" }}
      >
        <directionalLight
          position={[5, 5, 5]}
          intensity={2.5}
          castShadow
          shadow-mapSize={[1024, 1024]}
        />
        <ambientLight intensity={0.5} />
        <ContactShadows
          position={[0, -0.8, 0]}
          opacity={0.25}
          scale={10}
          blur={1.5}
          far={0.8}
        />
        <OrbitControls
          enableZoom
          enablePan
          target={[0, 0, 0]}
          enableRotate
          rotateSpeed={3.0}
        />
        {glb?.customize_file && (
          <GlbViewer
            customizeFile={glb.customize_file}
            settings={glb.settings}
            onLoaded={handleLoaded}
          />
        )}
      </Canvas>
    </div>
  );
};

export default ViewGlbPage;